// src/utils/adminHelper.js - Secure Admin Role Verification Helper
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Verify if current user has admin role via Firestore
 * @param {Object} currentUser - Firebase auth user object  
 * @returns {Promise<Object>} { isAdmin: boolean, error: string|null, userData: Object|null }
 */
export const verifyAdminRole = async (currentUser) => {
  try {
    if (!currentUser || !currentUser.email) {
      return { 
        isAdmin: false, 
        error: 'Niet ingelogd',
        userData: null 
      };
    }

    // Load user profile from Firestore users collection
    const userDoc = await getDoc(doc(db, 'users', currentUser.email));
    
    if (!userDoc.exists()) {
      console.warn('User profile niet gevonden:', currentUser.email);
      return { 
        isAdmin: false, 
        error: 'Gebruikersprofiel niet gevonden',
        userData: null 
      };
    }

    const userData = userDoc.data();
    
    // Check if user has admin role
    const isAdmin = userData.role === 'admin';
    
    console.log(`Admin verificatie voor ${currentUser.email}:`, {
      role: userData.role,
      isAdmin: isAdmin,
      organizationId: userData.organizationId
    });

    return {
      isAdmin: isAdmin,
      error: null,
      userData: userData
    };

  } catch (error) {
    console.error('Admin verificatie fout:', error);
    return {
      isAdmin: false,
      error: 'Verificatie mislukt: ' + error.message,
      userData: null
    };
  }
};

/**
 * Get admin dashboard statistics from Firestore
 * @returns {Promise<Object>} { success: boolean, data: Object|null, error: string|null }
 */
export const getAdminDashboardStats = async () => {
  try {
    // Import wat we nodig hebben
    const { collection, getDocs, getCountFromServer } = await import('firebase/firestore');
    
    // Tel teams
    const teamsSnapshot = await getDocs(collection(db, 'teams'));
    const totalTeams = teamsSnapshot.size;
    
    // Tel responses (quiz invullingen)
    const responsesSnapshot = await getDocs(collection(db, 'responses'));
    const totalResponses = responsesSnapshot.size;
    
    // Tel email jobs
    const emailJobsSnapshot = await getDocs(collection(db, 'email_jobs'));
    const emailJobs = emailJobsSnapshot.docs.map(doc => doc.data());
    
    // Tel daadwerkelijk verstuurde emails (optie 1: tel alle email_jobs)
    const totalEmailJobs = emailJobsSnapshot.size;
    
    // Tel daadwerkelijk verstuurde emails (optie 2: tel results arrays)
    let emailsSent = 0;
    emailJobs.forEach(job => {
      if (job.results && Array.isArray(job.results)) {
        emailsSent += job.results.length;
      } else if (job.totalSent) {
        emailsSent += job.totalSent;
      }
    });
    
    // Tel invitations (vanuit invitations_index)
    const invitationsSnapshot = await getDocs(collection(db, 'invitations_index'));
    const totalInvitations = invitationsSnapshot.size;

    const stats = {
      totalTeams: totalTeams,
      totalResponses: totalResponses,
      totalInvitations: totalInvitations,
      emailsSent: emailsSent || totalEmailJobs, // Fallback naar aantal jobs
      lastUpdated: new Date().toISOString()
    };

    console.log('📊 Dashboard stats geladen:', stats);

    return {
      success: true,
      data: stats,
      error: null
    };

  } catch (error) {
    console.error('Admin stats fout:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};